import type { AuditEntry } from "./types";

/**
 * Turning two state blobs into "what changed". Spec 9.
 *
 * The server sends `beforeState` and `afterState` exactly as the route recorded
 * them and deliberately does not summarise them (`api/routes/audit.ts`): a
 * server-side sentence would be an opinion about a row written long after the row.
 * The summarising happens here, where it can be changed without rewriting history.
 *
 * Nothing in this module renames a field. The keys are the server's own, and the
 * one workflow this page exists to support is reading a value here and grepping
 * the logs for it — which a display name would break.
 */

/** What an entry did, read off which of the two states it carries. */
export type EntryShape = "created" | "deleted" | "changed" | "recorded";

export interface FieldChange {
  readonly key: string;
  /** `undefined` means the state did not carry this field at all. */
  readonly before: string | undefined;
  readonly after: string | undefined;
}

/** The key a non-object state is shown under, having no field names of its own. */
const SCALAR_KEY = "value";

export function entryShape(entry: Pick<AuditEntry, "beforeState" | "afterState">): EntryShape {
  const hasBefore = !isAbsent(entry.beforeState);
  const hasAfter = !isAbsent(entry.afterState);

  if (hasBefore && hasAfter) return "changed";
  if (hasAfter) return "created";
  if (hasBefore) return "deleted";

  /**
   * Neither side. A rejected sign-in is the common case and it is worth showing —
   * it is what an intrusion attempt looks like — so it gets a shape of its own
   * rather than being described as the creation of nothing.
   */
  return "recorded";
}

/**
 * The fields that differ, in the order the *after* state lists them, followed by
 * anything only the before state had.
 *
 * Declaration order rather than alphabetical, for `NavRail`'s reason: the route
 * chose an order when it built the object, and sorting would discard it.
 */
export function changedFields(before: unknown, after: unknown): readonly FieldChange[] {
  // Neither side: nothing to diff, and `entryShape` already says what that means.
  if (isAbsent(before) && isAbsent(after)) return [];

  const beforeFields = isAbsent(before) ? EMPTY : fieldsOf(before);
  const afterFields = isAbsent(after) ? EMPTY : fieldsOf(after);

  /**
   * One side is present and is not a thing with fields, so there is nothing to
   * line up key by key. An absent opposite stays absent rather than becoming the
   * string `null`, which is what tells the page to print an em dash.
   */
  if (beforeFields === undefined || afterFields === undefined) {
    return scalarChange(before, after);
  }

  const onlyBefore = [...beforeFields.keys()].filter((key) => !afterFields.has(key));

  return [...afterFields.keys(), ...onlyBefore]
    .filter((key) => moved(beforeFields, afterFields, key))
    .map((key) => ({
      key,
      before: renderPresent(beforeFields, key),
      after: renderPresent(afterFields, key),
    }));
}

/**
 * A value as a person reads it: a string is itself, everything else is JSON.
 *
 * `null` prints as the word, because a stored null is a decision — an unnamed
 * station, a cleared external reference — and it has to read differently from a
 * field the state never carried at all, which the page prints as an em dash.
 */
export function renderValue(value: unknown): string {
  if (typeof value === "string") return value;

  return JSON.stringify(value) ?? String(value);
}

/**
 * The target types to offer as filters, taken from the page rather than from a
 * fixed list — `MessageTail`'s argument: a dropdown offering thirty kinds of
 * target of which this deployment has four is a worse control than one offering
 * the four.
 *
 * Alphabetical here, unlike the fields above: these are a set with no meaningful
 * order, and a select whose options move as the page turns is hard to use.
 */
export function targetTypesOf(rows: readonly AuditEntry[]): readonly string[] {
  return [...new Set(rows.map((row) => row.targetType))].sort();
}

/** Shared because an absent state has the same no fields every time. */
const EMPTY: ReadonlyMap<string, unknown> = new Map();

/**
 * The state's own fields, or `undefined` when it is not a thing with fields.
 *
 * An array is deliberately not a thing with fields: one row per index would read
 * as five changes where the entry recorded one list.
 */
function fieldsOf(state: unknown): ReadonlyMap<string, unknown> | undefined {
  if (typeof state !== "object" || state === null || Array.isArray(state)) return undefined;

  return new Map(Object.entries(state));
}

/** One row for a state that has no fields of its own, when it moved at all. */
function scalarChange(before: unknown, after: unknown): readonly FieldChange[] {
  if (!isAbsent(before) && !isAbsent(after) && canonical(before) === canonical(after)) return [];

  return [
    {
      key: SCALAR_KEY,
      before: isAbsent(before) ? undefined : renderValue(before),
      after: isAbsent(after) ? undefined : renderValue(after),
    },
  ];
}

/**
 * Whether a key's value really differs, which is not the same question as whether
 * its rendering does: `{a,b}` and `{b,a}` are one state written twice, and a row
 * claiming they differ would be a change nobody made.
 */
function moved(
  before: ReadonlyMap<string, unknown>,
  after: ReadonlyMap<string, unknown>,
  key: string,
): boolean {
  if (before.has(key) !== after.has(key)) return true;

  return canonical(before.get(key)) !== canonical(after.get(key));
}

function renderPresent(fields: ReadonlyMap<string, unknown>, key: string): string | undefined {
  return fields.has(key) ? renderValue(fields.get(key)) : undefined;
}

/** A value's identity, independent of the order its keys were written in. */
function canonical(value: unknown): string {
  if (typeof value !== "object" || value === null) return JSON.stringify(value) ?? "undefined";
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;

  return `{${Object.entries(value)
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .map(([key, nested]) => `${JSON.stringify(key)}:${canonical(nested)}`)
    .join(",")}}`;
}

/** No state at all, as opposed to a state whose value is `null`. */
function isAbsent(state: unknown): boolean {
  return state === null || state === undefined;
}
